import { useRef } from "react";
import { GripVertical, X, Plus } from "lucide-react";
import { PRATICA_VISIBLE_FIELDS, DEFAULT_PRATICA_FIELDS } from "../lib/anagConfig";

const CAMPI_BY_KEY = Object.fromEntries(PRATICA_VISIBLE_FIELDS.map((f) => [f.key, f]));

// Editor riusabile della sezione "Campi visibili in pratica": lista ordinabile (drag & drop) dei
// campi attivi + chip per aggiungere quelli inattivi. variant "dark" per i dialog a sfondo scuro
// fisso, "token" per i dialog che seguono il tema (contrasto ottimale in chiaro e scuro).
export default function CampiPraticaEditor({ value, onChange, defaultKeys = DEFAULT_PRATICA_FIELDS, testId = "campi-pratica", variant = "dark" }) {
  const active = Array.isArray(value) ? value : defaultKeys;
  const dragIdx = useRef(null);
  const toggle = (k) => onChange(active.includes(k) ? active.filter((x) => x !== k) : [...active, k]);
  const dropAt = (to) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from == null || from === to || from < 0 || from >= active.length) return;
    const arr = [...active]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); onChange(arr);
  };
  const inactive = PRATICA_VISIBLE_FIELDS.filter((f) => !active.includes(f.key));

  const S = variant === "token" ? {
    box: "border-border", head: "bg-muted/60", title: "text-foreground", sub: "text-muted-foreground",
    item: "border-primary/50 bg-primary/10", grip: "text-primary/70", num: "bg-primary/20 text-primary",
    label: "text-foreground", remove: "text-muted-foreground hover:text-red-500", empty: "text-muted-foreground",
    addLabel: "text-muted-foreground", chip: "border-border bg-background text-foreground hover:border-primary/60 hover:bg-primary/10",
  } : {
    box: "border-slate-700", head: "bg-slate-800/60", title: "text-slate-200", sub: "text-slate-400",
    item: "border-amber-500/50 bg-amber-500/10", grip: "text-amber-400/70", num: "bg-amber-500/20 text-amber-300",
    label: "text-slate-100", remove: "text-slate-400 hover:text-red-400", empty: "text-slate-500",
    addLabel: "text-slate-400", chip: "border-slate-600 bg-slate-900/40 text-slate-200 hover:border-amber-500/60 hover:bg-amber-500/10",
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${S.box}`} data-testid={testId}>
      <div className={`px-4 py-2.5 ${S.head}`}>
        <span className={`text-xs font-bold uppercase tracking-widest ${S.title}`}>Campi visibili in pratica</span>
        <p className={`text-[11px] mt-0.5 normal-case font-normal tracking-normal ${S.sub}`}>Scegli e trascina per ordinare i recapiti mostrati sotto al nominativo in ogni scheda pratica.</p>
      </div>
      <div className="p-4 space-y-3">
        <div className="space-y-1.5" data-testid={`${testId}-attivi`}>
          {active.length === 0 && <div className={`text-xs ${S.empty}`}>Nessun campo selezionato: nessun recapito verrà mostrato in pratica.</div>}
          {active.map((k, i) => {
            const f = CAMPI_BY_KEY[k];
            if (!f) return null;
            return (
              <div key={k} draggable
                onDragStart={(e) => { dragIdx.current = i; e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); dropAt(i); }}
                data-testid={`${testId}-attivo-${k}`}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-grab active:cursor-grabbing ${S.item}`}>
                <GripVertical className={`h-4 w-4 shrink-0 ${S.grip}`} />
                <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-mono shrink-0 ${S.num}`}>{i + 1}</span>
                <span className={`text-xs flex-1 ${S.label}`}>{f.label}</span>
                <button type="button" onClick={() => toggle(k)} data-testid={`${testId}-remove-${k}`}
                  className={`transition-colors ${S.remove}`} title={`Rimuovi ${f.label}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
        {inactive.length > 0 && (
          <div>
            <div className={`text-[11px] mb-1.5 ${S.addLabel}`}>Aggiungi campo:</div>
            <div className="flex flex-wrap gap-1.5">
              {inactive.map((f) => (
                <button key={f.key} type="button" onClick={() => toggle(f.key)} data-testid={`${testId}-add-${f.key}`}
                  className={`inline-flex items-center gap-1 text-xs rounded-full border px-2.5 py-1 transition-colors ${S.chip}`}>
                  <Plus className="h-3 w-3" /> {f.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
